import React, { useCallback, useEffect } from 'react';
import JobActions from 'components/JobActions';
import SideBar from 'components/SideBar';
import JourneyInfoSide from 'components/JourneyInfoSide';
import Spinner from 'components/Spinner';
import StopsDetails from 'components/StopsDetails';
import Documentation from 'components/Documentation';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'hooks/useAuth/useAuth';
import { getJobDetailUrl, fetcher } from 'utils/network/http';
import { ROUTES } from 'utils/constants/routes';
import config from 'utils/constants/config';
import { useHistory, useParams } from 'react-router-dom';
import FastVehicleCreation from 'components/FastVehicleCreation';
import { useVehicles } from 'hooks/useVehicles/useVehicles';
import useSWR from 'swr';

const PendingJobDetail = ({ className }) => {
  const { t } = useTranslation('PendingJobDetail');
  const { profile } = useAuth();
  const { id } = useParams();
  let history = useHistory();
  const {
    showFastVehicleCreation,
    suitableVehicles,
    fetchSuitableVehicles,
    loadingSuitableVehicles
  } = useVehicles();

  const { data: response, error, mutate: refreshJourney } = useSWR(
    profile ? getJobDetailUrl(id) : null,
    fetcher,
    {
      refreshInterval: config.FAST_POLLING_DELAY
    }
  );

  useEffect(() => {
    if (!suitableVehicles && !error && !loadingSuitableVehicles) {
      fetchSuitableVehicles(id);
    }
  }, [
    suitableVehicles,
    fetchSuitableVehicles,
    error,
    loadingSuitableVehicles,
    id
  ]);

  useEffect(() => {
    const journey = response?.data;
    if (journey && journey.status === 'IN_TRANSIT' && id) {
      history.push(`${ROUTES.IN_TRANSIT_DETAIL.DEFAULT}/${id}`);
    }
  }, [response, history, id]);

  const renderJob = useCallback(() => {
    const journey = response?.data;
    if (!journey && !error) {
      return (
        <div
          className="pending-job-detail__loading"
          data-testid="pending-job-detail__loading"
        >
          <Spinner />
        </div>
      );
    }

    if (error) {
      return (
        <div
          className="pending-job-detail__error"
          data-testid="pending-job-detail__error"
        >
          <span>{t('error')}</span>
        </div>
      );
    }

    if (journey && journey.status !== 'ASSIGNED') {
      return (
        <div
          className="pending-job-detail__error"
          data-testid="pending-job-detail__not-assigned"
        >
          <span>{t('errorNotAssigned')}</span>
        </div>
      );
    }

    if (journey)
      return (
        <>
          <SideBar
            backLabel={t('back')}
            redirectRoute={ROUTES.PENDING}
            hideLink={showFastVehicleCreation}
          >
            {!showFastVehicleCreation && (
              <>
                <JourneyInfoSide journey={journey} />
                <JobActions
                  assignedDriverId={journey.driver_id}
                  assignedVehiclesIds={journey.vehicles_id}
                  assignedDriverUrl={journey.driver}
                  assignedVehiclesUrls={journey.vehicles}
                  silentFetchJourney={refreshJourney}
                  journeyId={journey.id}
                  colleagues={profile.colleagues}
                  driverConfirmed={journey.driver_confirmed}
                />
                <Documentation
                  journeyId={journey.id}
                  journeyStatus={journey.status}
                />
              </>
            )}
            {showFastVehicleCreation && (
              <FastVehicleCreation
                journeyId={journey.id}
                refreshJourney={refreshJourney}
                currentAssignedVehicles={journey.vehicles_id}
              />
            )}
          </SideBar>
          <div
            className="pending-job-detail__body"
            data-testid="pending-job-detail__body"
          >
            <StopsDetails
              journey={journey}
              expanded
              showAdvanceButton={true}
              refetchData={refreshJourney}
            />
          </div>
        </>
      );
  }, [response, error, refreshJourney, profile, t, showFastVehicleCreation]);

  return (
    <div className={className} data-testid="pending-job-detail">
      <div className="pending-job-detail">{renderJob()}</div>
    </div>
  );
};

export default PendingJobDetail;
